import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';

// react-bootstrap turns `isInvalid` into a CSS class and nothing else, so an
// error message is visible to a sighted member and silent to a screen reader
// unless aria-invalid and aria-describedby are wired by hand. Every validated
// control on the listing form renders through here so that wiring cannot be
// left off one field.
//
// `required` answers WCAG 3.3.2 on both channels at once: the word in the
// label for a sighted member, and aria-required for a screen reader, which
// reads the state before the field is submitted rather than after. Marking it
// on one channel alone is what leaves the other half of the room guessing.
function ValidatedField({
    controlId,
    label,
    error = '',
    help = '',
    required = false,
    select = false,
    children,
    ...controlProps
}) {
    const describedBy =
        [error ? `${controlId}-feedback` : '', help ? `${controlId}-help` : '']
            .filter(Boolean)
            .join(' ') || undefined;
    const shared = {
        ...controlProps,
        required: required || undefined,
        'aria-required': required ? 'true' : undefined,
        isInvalid: Boolean(error),
        'aria-invalid': error ? 'true' : undefined,
        'aria-describedby': describedBy,
    };
    return (
        <Form.Group controlId={controlId} className="mb-3">
            <Form.Label>
                {label}
                {required && (
                    <span className="text-muted fw-normal"> (required)</span>
                )}
            </Form.Label>
            {select ? (
                <Form.Select {...shared}>{children}</Form.Select>
            ) : (
                <Form.Control {...shared} />
            )}
            {error && (
                <Form.Control.Feedback
                    id={`${controlId}-feedback`}
                    type="invalid"
                >
                    {error}
                </Form.Control.Feedback>
            )}
            {help && <Form.Text id={`${controlId}-help`}>{help}</Form.Text>}
        </Form.Group>
    );
}

ValidatedField.propTypes = {
    controlId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    error: PropTypes.string,
    help: PropTypes.node,
    required: PropTypes.bool,
    select: PropTypes.bool,
    children: PropTypes.node,
};

export default ValidatedField;
